import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { TicketMessageFormData } from '../../types';
import { useToast } from '../../components/shared';
import { ClockIcon, ExclamationTriangleIcon, CheckCircleIcon, XMarkIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

const TicketDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ticketId } = useParams<{ ticketId: string }>();
  const { user } = useAuth();
  const { tickets, fetchTicketMessages, addTicketMessage, releases } = useData();
  const { showToast } = useToast();

  const [ticket, setTicket] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [replyText, setReplyText] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Ticket ile ilişkili release'i bul
  const relatedRelease = ticket?.release_id ? releases.find(r => r.id === ticket.release_id) : null;

  const loadMessages = useCallback(async () => {
    if (ticketId) {
      try {
        const ticketMessages = await fetchTicketMessages(ticketId);
        setMessages(ticketMessages);
      } catch (error) {
        showToast('error', t('support.loadMessagesError'));
      }
    }
  }, [ticketId, fetchTicketMessages, showToast, t]);

  useEffect(() => {
    if (ticketId) {
      const foundTicket = tickets.find(t => t.id === ticketId);
      if (foundTicket) {
        setTicket(foundTicket);
        loadMessages();
      }
    }
  }, [ticketId, tickets, loadMessages]);

  const handleReply = async () => {
    if (!replyText.trim() || !ticketId || !user) return;

    try {
      setIsLoading(true);
      const messageData: TicketMessageFormData = {
        ticket_id: ticketId,
        content: replyText.trim(),
        is_admin_response: false
      };

      await addTicketMessage(messageData);
      setReplyText('');
      showToast('success', t('support.replyAdded'));
      await loadMessages();
    } catch (error) {
      showToast('error', t('support.replyError'));
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />;
      case 'in_progress': return <ClockIcon className="w-5 h-5 text-yellow-400" />;
      case 'resolved': return <CheckCircleIcon className="w-5 h-5 text-green-400" />;
      default: return <ExclamationTriangleIcon className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'in_progress': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'resolved': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'text-blue-400';
      case 'normal': return 'text-gray-400';
      case 'high': return 'text-orange-400';
      case 'urgent': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  if (!ticket) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-800/30 backdrop-blur-sm rounded-lg p-8 text-center">
            <h1 className="text-2xl font-bold text-white mb-4">{t('support.ticketNotFound')}</h1>
            <button
              onClick={() => navigate('/support')}
              className="bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              {t('support.backToSupport')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-gray-800/30 backdrop-blur-sm rounded-lg p-6 border border-gray-700/50">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/support')}
              className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
              <span>{t('support.backToSupport')}</span>
            </button>
            <div className="flex items-center space-x-4">
              <div className={`flex items-center space-x-2 px-3 py-1 rounded-full border ${getStatusColor(ticket.status)}`}>
                {getStatusIcon(ticket.status)}
                <span className="text-sm font-medium">{t(`support.statuses.${ticket.status}`)}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h1 className="text-2xl font-bold text-white mb-4">{ticket.subject}</h1>
              <div className="space-y-2 text-gray-300">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">{t('support.ticketId')}:</span>
                  <span className="font-mono text-sm">{ticket.id}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">{t('support.created')}:</span>
                  <span className="text-sm">{formatDate(ticket.created_at)}</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">{t('support.category')}:</span>
                <span className="text-sm text-gray-300">{t(`support.categories.${ticket.category}`)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">{t('support.priority')}:</span>
                <span className={`text-sm font-medium ${getPriorityColor(ticket.priority)}`}>
                  {t(`support.priorities.${ticket.priority}`)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">{t('support.lastUpdate')}:</span>
                <span className="text-sm text-gray-300">{formatDate(ticket.updated_at)}</span>
              </div>
            </div>
          </div>

          {/* Related Release Section */}
          {relatedRelease && (
            <div className="mt-6 pt-6 border-t border-gray-700/50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                  <InformationCircleIcon className="w-5 h-5" />
                  <span>{t('support.relatedMusic')}</span>
                </h3>
                <button
                  onClick={() => navigate(`/tracks/${relatedRelease.id}`)}
                  className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2"
                >
                  <InformationCircleIcon className="w-4 h-4" />
                  <span>{t('support.viewMusicDetails')}</span>
                </button>
              </div>
              <div className="bg-gray-700/30 rounded-lg p-4">
                <div className="flex items-center space-x-4">
                  {relatedRelease.cover_art && (
                    <img 
                      src={relatedRelease.cover_art} 
                      alt={relatedRelease.title}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <h4 className="text-white font-semibold">{relatedRelease.title}</h4>
                    <p className="text-gray-400 text-sm">
                      {relatedRelease.type} • {relatedRelease.genre}
                    </p>
                    <p className="text-gray-400 text-sm">
                      {t('tracks.releaseDate')}: {new Date(relatedRelease.release_date).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                      relatedRelease.status === 'published' ? 'bg-green-500/20 text-green-400' :
                      relatedRelease.status === 'approved' ? 'bg-blue-500/20 text-blue-400' :
                      relatedRelease.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {t(`tracks.statuses.${relatedRelease.status}`)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {ticket.description && (
            <div className="mt-6 pt-6 border-t border-gray-700/50">
              <h3 className="text-lg font-semibold text-white mb-3">{t('support.initialMessage')}</h3>
              <p className="text-gray-300 whitespace-pre-wrap">{ticket.description}</p>
            </div>
          )}
        </div>

        {/* Messages Section */}
        <div className="bg-gray-800/30 backdrop-blur-sm rounded-lg p-6 border border-gray-700/50">
          <h3 className="text-lg font-semibold text-white mb-4">
            {t('support.ticketThread')}
          </h3>

          <div className="space-y-4 max-h-96 overflow-y-auto">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.is_admin_response ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-xs lg:max-w-md ${
                  message.is_admin_response 
                    ? 'bg-gradient-to-r from-purple-500 to-violet-600' 
                    : 'bg-gray-700/40 border border-gray-600/30'
                } rounded-lg p-4 backdrop-blur-sm`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        message.is_admin_response ? 'bg-white/20' : 'bg-gray-600'
                      }`}>
                        <span className="text-white text-xs font-medium">
                          {message.is_admin_response ? 'A' : ticket.artist_name?.charAt(0) || 'U'}
                        </span>
                      </div>
                      <span className="text-xs text-gray-300">
                        {message.is_admin_response ? 'Admin' : ticket.artist_name || 'You'}
                      </span>
                    </div>
                    <span className="text-xs text-gray-400">{formatDate(message.created_at)}</span>
                  </div>
                  <p className="text-white text-sm whitespace-pre-wrap">{message.content}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reply Section */}
        <div className="bg-gray-800/30 backdrop-blur-sm rounded-lg p-6 border border-gray-700/50">
          <h3 className="text-lg font-semibold text-white mb-4">{t('support.replyToTicket')}</h3>
          <div className="space-y-4">
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={t('support.form.replyPlaceholder')}
              rows={4}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm transition-all duration-200 resize-none"
            />
            <div className="flex justify-end">
              <button
                onClick={handleReply}
                disabled={!replyText.trim() || isLoading}
                className="bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 disabled:from-gray-600 disabled:to-gray-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
              >
                {isLoading ? t('common.sending') : t('support.sendReply')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetail; 